import { useState, useRef } from 'react';

import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import BpkSmallArrowDownIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/arrow-down';
import BpkPopover from '@skyscanner/backpack-web/bpk-component-popover';
import BpkText from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import type { PaymentMethodPolicyDetails } from '@shared/types/hotel-content';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './PaymentCardPolicy.module.scss';

import { paymentCard } from '@shared/ui/components/HotelPolicyMapping';
import { withI18n } from '@shared/ui/contexts/i18n';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  policy: PaymentMethodPolicyDetails;
  key: string;
};

const PaymentCardPolicy = ({ i18n: { translate }, key, policy }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const targetRef = useRef<HTMLSpanElement>(null);

  const openPopover = () => setIsOpen(true);
  const closePopover = () => setIsOpen(false);

  const { paymentMethods } = policy;

  const paymentCardsWithLogo: string[] = [];
  const paymentCardsWithoutLogo: string[] = [];

  paymentMethods.forEach((cardType) => {
    if (paymentCard[cardType]) {
      paymentCardsWithLogo.push(cardType);
    } else {
      paymentCardsWithoutLogo.push(cardType);
    }
  });

  return (
    <div key={key} className={STYLES.PaymentCardPolicy}>
      <BpkText className={cls('PaymentCardPolicy__title')}>
        {policy.title}
      </BpkText>
      <div className={STYLES.PaymentCardPolicy__cardLogo}>
        {paymentCardsWithLogo.map((cardType) => (
          <img src={paymentCard[cardType]} alt="card logo" key={cardType} />
        ))}
        {paymentCardsWithoutLogo.length > 0 &&
          paymentCardsWithoutLogo.map((cardType) => (
            <BpkText
              tagName="p"
              key={cardType}
              className={cls(
                'PaymentCardPolicy__cardTextOnMobile',
                'PaymentCardPolicy__hideAboveMobile',
              )}
            >
              {cardType}
            </BpkText>
          ))}
        {paymentCardsWithoutLogo.length > 0 && (
          <div
            id="payment-cards-popover-container"
            className={cls('PaymentCardPolicy__hideOnMobile')}
          >
            <BpkPopover
              id="payment-cards-popover"
              target={
                <span ref={targetRef}>
                  <BpkButtonV2
                    onClick={openPopover}
                    type={BUTTON_TYPES.link}
                    className={STYLES.PaymentCardPolicy__seeMoreCards}
                  >
                    {translate('Button_viewMore')}
                    <BpkSmallArrowDownIcon />
                  </BpkButtonV2>
                </span>
              }
              onClose={closePopover}
              isOpen={isOpen}
              label="Payment cards popover"
              closeButtonText={translate('Modal_close')}
              closeButtonProps={{
                tabIndex: 0,
              }}
            >
              {paymentCardsWithoutLogo.map((cardType) => (
                <BpkText
                  tagName="p"
                  key={cardType}
                  className={STYLES.PaymentCardPolicy__cardWithoutLogo}
                >
                  {cardType}
                </BpkText>
              ))}
            </BpkPopover>
          </div>
        )}
      </div>
    </div>
  );
};

export default withI18n(PaymentCardPolicy);
